import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Divider,
    Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import AddProductDialog from '../components/product/AddProductDialog';
import { Product } from '../types';
import { getProducts } from '../services/api';
import ProductCardPage from './ProductCardPage';
import { MainLayoutContainer, Sidebar } from '../components/common/ResponsiveLayout';
import { useTranslation } from 'react-i18next';

const ProductCatalogPage: React.FC = () => {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const { data: products = [], isLoading, error } = useQuery<Product[]>({
        queryKey: ['products'],
        queryFn: getProducts,
    });
    const queryClient = useQueryClient();

    const { productId } = useParams<{ productId?: string }>();
    const { t } = useTranslation();

    useEffect(() => {
        if (productId && products.length > 0) {
            const product = products.find(p => p.id === Number(productId));
            if (product) setSelectedProduct(product);
        }
    }, [productId, products]);

    // Сортируем продукты по имени
    const sortedProducts = useMemo(() => {
        return [...products].sort((a, b) => a.name.localeCompare(b.name));
    }, [products]);

    return (
        <MainLayoutContainer
            sx={{
                height: ['100dvh', '100svh', '100vh'], // динамическая высота для разных браузеров
                overflow: 'hidden',
            }}
        >
            {/* Левая колонка — список продуктов */}
            <Sidebar
                component={Paper}
                sx={{
                    p: 1,
                    width: 250,
                    height: 'calc(100vh - 32px)', // корректировка по высоте с учётом отступа
                    mt: 1.5,                      // небольшой верхний отступ (~12px)
                    overflowY: 'auto',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 1 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddDialogOpen(true)}
                        sx={{
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 1,
                            bgcolor: 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        {t('productCatalog.new')} {/* Локализация */}
                    </Button>
                </Box>
                <List dense>
                    {sortedProducts.map((product) => (
                        <ListItemButton
                            key={product.id}
                            selected={selectedProduct?.id === product.id}
                            onClick={() => setSelectedProduct(product)}
                        >
                            <ListItemText primary={product.name} />
                        </ListItemButton>
                    ))}
                </List>
            </Sidebar>

            {/* Правая рабочая область */}
            <Box component="main" sx={{ flexGrow: 1, p: 2, height: '100%', overflow: 'hidden' }}>
                {!selectedProduct && (
                    <Box
                        sx={{
                            height: '80vh',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="h5" color="text.secondary">
                            {t('productCatalog.selectProduct')} {/* Локализация */}
                        </Typography>
                    </Box>
                )}

                {selectedProduct && (
                    <ProductCardPage selectedProductId={selectedProduct.id} />
                )}
            </Box>
            <AddProductDialog
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                onSuccess={() => {
                    setIsAddDialogOpen(false);
                    queryClient.invalidateQueries(['products']);
                }}
            />
        </MainLayoutContainer>
    );
};

export default ProductCatalogPage;
